import React from 'react';
import { Link } from 'react-router-dom';
import { Map as MapIcon, Globe2 } from 'lucide-react';
import { Button, Card } from '@/components/eds';
import { useInvestorGisEntitlement } from '@/hooks/useInvestorGisEntitlement';
import { hasInvestorGis, investorAuthorizedLayers } from '@/lib/investorGis';

// R11P GEOLOGICAL P0.5-P9E — Investor Dashboard entry to the Investor GIS.
//
// The CTA appears ONLY when the server entitlement contract returns at least
// one layer with access 'investor' and operations.view === true. Investor
// status, verification, agreements, applications and role are never consulted.
// A failed entitlement read is "unavailable" — never presented as no access —
// and absence of access names no reason (no expired/revoked claim).

const linkClass =
  'text-sm font-semibold text-eds-emerald-2 underline underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-md';

const InvestorGisEntry: React.FC = () => {
  const { snapshot, reload } = useInvestorGisEntitlement();
  const available = hasInvestorGis(snapshot);
  const count = available ? investorAuthorizedLayers(snapshot.layers).length : null;

  const publicLink = (
    <Link to="/gis" className={linkClass}>
      <Globe2 className="mr-1 inline h-4 w-4" aria-hidden="true" />
      Open Public GIS
    </Link>
  );

  let body: React.ReactNode;
  switch (snapshot.state) {
    case 'available':
      body = (
        <>
          <p className="font-eds-body text-sm text-eds-fg">
            Investor GIS access is available for this account
            {count !== null ? ` (${count} authorized layer${count === 1 ? '' : 's'})` : ''}.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <Link
              to="/investor/gis"
              className="inline-flex items-center rounded-eds-md bg-eds-forest px-3 py-2 text-sm font-semibold text-white hover:bg-eds-forest-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
            >
              Open Investor GIS
            </Link>
            {publicLink}
          </div>
        </>
      );
      break;
    case 'no_access':
      body = (
        <>
          <p className="font-eds-body text-sm text-eds-fg">
            No current Investor GIS access is available for this account.
          </p>
          <div className="mt-3">{publicLink}</div>
        </>
      );
      break;
    case 'profile_required':
      body = (
        <>
          <p className="font-eds-body text-sm text-eds-fg">
            Investor profile required. Investor GIS is available only to accounts
            with an investor profile.
          </p>
          <div className="mt-3">{publicLink}</div>
        </>
      );
      break;
    case 'auth_required':
      body = (
        <p className="font-eds-body text-sm text-eds-fg">
          Please sign in again to check Investor GIS access.
        </p>
      );
      break;
    case 'unavailable':
      body = (
        <>
          <p className="font-eds-body text-sm text-eds-fg">
            Investor GIS access status is temporarily unavailable. This does not
            mean access has changed.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <Button variant="secondary" size="sm" onClick={reload}>
              Try again
            </Button>
            {publicLink}
          </div>
        </>
      );
      break;
    default:
      body = (
        <p role="status" aria-live="polite" className="font-eds-body text-sm text-eds-muted-fg">
          Checking Investor GIS access…
        </p>
      );
  }

  return (
    <Card className="p-5" elevation="none" aria-labelledby="investor-gis-entry-heading">
      <div className="mb-2 flex items-center gap-2">
        <MapIcon className="h-5 w-5 text-eds-emerald-2" aria-hidden="true" />
        <h3 id="investor-gis-entry-heading" className="font-eds-display font-semibold text-eds-fg">
          Investor GIS
        </h3>
      </div>
      <div aria-live="polite">{body}</div>
      <p className="mt-3 font-eds-body text-xs text-eds-muted-fg">
        Additional geological and spatial information is made available only through
        GIISDEC-approved access, determined by the platform&apos;s authorization
        controls. Public GIS remains separately available to everyone.
      </p>
    </Card>
  );
};

export default InvestorGisEntry;
